export interface Hsv {
	/** 0 to 360 */
	h: number
	/** 0 to 1 */
	s: number
	/** 0 to 1 */
	v: number
}

const HEX = /^#?([\da-f]{3}|[\da-f]{6})$/i

/** A `#rrggbb` string for a typed value, or null when it is not a colour. */
export function normalizeHex(input: string): string | null {
	const match = HEX.exec(input.trim())
	if (!match?.[1]) return null
	const digits =
		match[1].length === 3
			? [...match[1]].map((digit) => digit + digit).join("")
			: match[1]
	return `#${digits.toLowerCase()}`
}

/** Hue, saturation and value for a hex colour, or null when it is not one. */
export function hexToHsv(input: string): Hsv | null {
	const hex = normalizeHex(input)
	if (!hex) return null
	const r = parseInt(hex.slice(1, 3), 16) / 255
	const g = parseInt(hex.slice(3, 5), 16) / 255
	const b = parseInt(hex.slice(5, 7), 16) / 255
	const max = Math.max(r, g, b)
	const delta = max - Math.min(r, g, b)
	let h = 0
	if (delta > 0) {
		if (max === r) h = ((g - b) / delta) % 6
		else if (max === g) h = (b - r) / delta + 2
		else h = (r - g) / delta + 4
		h *= 60
		if (h < 0) h += 360
	}
	return { h, s: max === 0 ? 0 : delta / max, v: max }
}

/** The `#rrggbb` string for a hue, saturation and value, each clamped to its range. */
export function hsvToHex({ h, s, v }: Hsv): string {
	const hue = ((h % 360) + 360) % 360
	const sat = Math.min(1, Math.max(0, s))
	const val = Math.min(1, Math.max(0, v))
	const channel = (n: number) => {
		const k = (n + hue / 60) % 6
		const value = val - val * sat * Math.max(0, Math.min(k, 4 - k, 1))
		return Math.round(value * 255)
			.toString(16)
			.padStart(2, "0")
	}
	return `#${channel(5)}${channel(3)}${channel(1)}`
}
